// Signage → Salud del día (/signage/salud). Consejos de salud (editable) con una
// condición de clima opcional y un producto sugerido. La escena `salud` del
// reproductor lee el clima/UV del local y muestra el consejo que corresponde.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HeartPulse, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { sigInsert, sigList, sigSoftDelete, sigUpdate } from './shared';

interface Tip {
  id: string; texto: string; icono: string | null; condicion: string; producto_id: string | null;
  cta: string | null; orden: number; es_activo: boolean;
}
interface ProdRow { id: string; codigo: string; nombre: string }

const CONDS = [
  { v: 'siempre', l: 'Siempre (cualquier clima)' },
  { v: 'sol', l: '☀️ Sol / UV alto' },
  { v: 'frio', l: '🧣 Frío' },
  { v: 'lluvia', l: '🌧️ Lluvia' },
  { v: 'calor', l: '🔥 Calor' },
];
const condLabel = (c: string) => CONDS.find((x) => x.v === c)?.l ?? c;
const vacio = { texto: '', icono: '💧', condicion: 'siempre', producto_id: '', cta: '', orden: 0, es_activo: true };

export function Salud() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: tips = [], isFetching } = useQuery<Tip[]>({
    queryKey: ['sig-salud'], queryFn: () => sigList<Tip>('salud_tip?select=*&deleted_at=is.null&order=orden,texto'),
  });

  const [abierto, setAbierto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(vacio);
  const [prodNombre, setProdNombre] = useState('');
  const [busq, setBusq] = useState('');

  const { data: resultados = [] } = useQuery<ProdRow[]>({
    queryKey: ['sig-salud-prod', busq], enabled: abierto && busq.trim().length >= 2,
    queryFn: async () => (await apiClient.get<ProdRow[]>(
      `/rest/inventario.productos?select=id,codigo,nombre&es_activo=eq.true&deleted_at=is.null&nombre=ilike.*${encodeURIComponent(busq.trim())}*&order=nombre&limit=20`)).data,
    staleTime: 30_000,
  });

  const abrir = async (t?: Tip) => {
    setBusq('');
    if (t) {
      setEditId(t.id);
      setForm({ texto: t.texto, icono: t.icono ?? '', condicion: t.condicion, producto_id: t.producto_id ?? '', cta: t.cta ?? '', orden: t.orden, es_activo: t.es_activo });
      if (t.producto_id) {
        const p = (await apiClient.get<ProdRow[]>(`/rest/inventario.productos?select=id,codigo,nombre&id=eq.${t.producto_id}`)).data[0];
        setProdNombre(p?.nombre ?? '');
      } else setProdNombre('');
    } else { setEditId(null); setForm(vacio); setProdNombre(''); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      if (!form.texto.trim()) throw new Error('El consejo es obligatorio');
      const body = { texto: form.texto.trim(), icono: form.icono || null, condicion: form.condicion,
        producto_id: form.producto_id || null, cta: form.cta || null, orden: Number(form.orden) || 0, es_activo: form.es_activo };
      if (editId) await sigUpdate('salud_tip', editId, body); else await sigInsert('salud_tip', body);
    },
    onSuccess: () => { toast.success('Consejo guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['sig-salud'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => sigSoftDelete('salud_tip', id),
    onSuccess: () => { toast.success('Consejo eliminado'); void qc.invalidateQueries({ queryKey: ['sig-salud'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><HeartPulse className="w-5 h-5" /> Salud del día</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Consejos de salud que rotan en la pantalla (escena "Salud del día"). Si defines una condición de clima, el consejo aparece cuando corresponde (el reproductor lee el clima/UV del local). Opcional: un producto sugerido.</p>
        </div>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo consejo</Button>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Consejos ({tips.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Consejo</th><th className="py-2 pr-3">Condición</th><th className="py-2 pr-3">Producto</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {tips.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin consejos. Crea el primero (p. ej. "Hidrátate: 8 vasos al día").</td></tr>}
              {tips.map((t) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{t.icono} {t.texto}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{condLabel(t.condicion)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.producto_id ? '✓' : '—'}</td>
                  <td className="py-2 pr-3">{t.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(t)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar('¿Eliminar este consejo?', { peligro: true })) eliminar.mutate(t.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isFetching && <p className="text-xs text-muted-foreground mt-2">Cargando…</p>}
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar consejo' : 'Nuevo consejo'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-[5rem_1fr] gap-3">
              <Campo label="Ícono"><input className={`${fieldCls} text-center text-lg`} value={form.icono} onChange={(e) => setForm({ ...form, icono: e.target.value })} placeholder="💧" /></Campo>
              <Campo label="Consejo *"><input className={fieldCls} value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} placeholder="Hidrátate: toma 8 vasos de agua al día" /></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Condición de clima">
                <select className={fieldCls} value={form.condicion} onChange={(e) => setForm({ ...form, condicion: e.target.value })}>
                  {CONDS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} /></Campo>
            </div>

            {/* Producto sugerido (opcional) */}
            <div className="border rounded-md p-3 space-y-2">
              <div className="text-sm font-medium">Producto sugerido <span className="text-muted-foreground font-normal">(opcional)</span></div>
              {form.producto_id ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate">{prodNombre || 'Producto seleccionado'}</span>
                  <button className="text-red-500 text-xs hover:underline" onClick={() => { setForm({ ...form, producto_id: '' }); setProdNombre(''); }}>Quitar</button>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex items-center gap-2 border rounded-md px-2">
                    <Search className="w-4 h-4 text-muted-foreground" />
                    <input className="flex-1 bg-transparent py-1.5 text-sm outline-none" placeholder="Buscar producto…" value={busq} onChange={(e) => setBusq(e.target.value)} />
                  </div>
                  {busq.trim().length >= 2 && resultados.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto border rounded-md bg-popover shadow-lg">
                      {resultados.map((p) => (
                        <button key={p.id} className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted truncate"
                          onClick={() => { setForm({ ...form, producto_id: p.id }); setProdNombre(p.nombre); setBusq(''); }}>{p.nombre}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {form.producto_id && <Campo label="Llamado a la acción"><input className={fieldCls} value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} placeholder="Recomendado hoy" /></Campo>}
            </div>

            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.texto.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
